import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { Newspaper, ExternalLink, Clock } from 'lucide-react';
import { Badge } from './ui/badge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CryptoNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/news?limit=15`);
      if (response.data.success) {
        setNews(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Recently';
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      
      if (diffInSeconds < 60) return 'Just now';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
      return `${Math.floor(diffInSeconds / 86400)}d ago`;
    } catch {
      return 'Recently';
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    // Remove HTML tags
    const cleanText = text.replace(/<[^>]*>/g, '');
    if (cleanText.length <= maxLength) return cleanText;
    return cleanText.substring(0, maxLength) + '...';
  };

  return (
    <Card className="border" style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }} data-testid="crypto-news-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: '#1D4ED8' }}>
            <Newspaper className="w-5 h-5 text-white" />
          </div>
          <div>
            <CardTitle className="text-lg" style={{ color: '#0F172A' }}>Crypto News</CardTitle>
            <p className="text-sm" style={{ color: '#475569' }}>Latest from Cointelegraph</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 border-4 rounded-full"
              style={{ borderColor: '#1D4ED8', borderTopColor: 'transparent' }}
            />
          </div>
        ) : (
          <ScrollArea className="h-[600px]">
            <div className="space-y-1 p-4 pt-0" data-testid="news-list">
              {news.map((item, index) => (
                <motion.a
                  key={index}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.02, x: 4, backgroundColor: '#F8FAFC' }}
                  className="block p-4 rounded-lg border transition-all cursor-pointer"
                  style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB' }}
                  data-testid={`news-item-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="secondary" className="text-xs" style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8' }}>
                          {item.source}
                        </Badge>
                        <div className="flex items-center gap-1 text-xs" style={{ color: '#475569' }}>
                          <Clock className="w-3 h-3" />
                          <span>{formatTimeAgo(item.published)}</span>
                        </div>
                      </div>
                      
                      <h3 className="font-semibold mb-2 line-clamp-2" style={{ color: '#0F172A' }}>
                        {item.title}
                      </h3>
                      
                      {item.summary && (
                        <p className="text-sm line-clamp-2 mb-2" style={{ color: '#475569' }}>
                          {truncateText(item.summary, 150)}
                        </p>
                      )}
                      
                      {item.author && (
                        <p className="text-xs" style={{ color: '#475569' }}>
                          By {item.author}
                        </p>
                      )}
                    </div>
                    
                    <ExternalLink className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: '#475569' }} />
                  </div>
                </motion.a>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default CryptoNews;
